import { X, Edit2, Trash2, Calendar } from 'lucide-react';
import { useFunding, type CommitmentWithDetails } from '../../hooks/useFunding';

interface Props {
  commitment: CommitmentWithDetails;
  onClose: () => void;
  onEdit: () => void;
}

export function FundingCommitmentDetailModal({ commitment, onClose, onEdit }: Props) {
  const { deleteDisbursement, deleteCommitment } = useFunding();

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date?: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      planned: 'Prevu',
      promised: 'Promis',
      signed: 'Signe',
      in_progress: 'En cours',
      paid: 'Verse',
      cancelled: 'Annule'
    };
    return labels[status] || status;
  };

  const handleDeleteDisbursement = async (id: string) => {
    if (confirm('Supprimer ce versement ?')) {
      try {
        await deleteDisbursement(id);
      } catch (error) {
        console.error('Error deleting disbursement:', error);
      }
    }
  };

  const handleDeleteCommitment = async () => {
    if (commitment.disbursements.length > 0) {
      alert('Impossible de supprimer un engagement avec des versements. Supprimez d\'abord les versements.');
      return;
    }

    if (confirm('Supprimer cet engagement ?')) {
      try {
        await deleteCommitment(commitment.id);
        onClose();
      } catch (error) {
        console.error('Error deleting commitment:', error);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">
            Detail de l'engagement
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Header Info */}
          <div className="bg-gradient-to-r from-emerald-50 to-blue-50 p-6 rounded-lg">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{commitment.source?.name}</h3>
                <p className="text-gray-600 mt-1">{commitment.title}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-sm px-2 py-1 bg-white rounded-full">
                    {commitment.source?.source_type}
                  </span>
                  <span className="text-sm px-2 py-1 bg-white rounded-full">
                    {getStatusLabel(commitment.status)}
                  </span>
                  {commitment.phase_tag && (
                    <span className="text-sm px-2 py-1 bg-white rounded-full">
                      {commitment.phase_tag}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={onEdit}
                  className="p-2 text-gray-600 hover:text-emerald-600 hover:bg-white rounded-lg transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={handleDeleteCommitment}
                  className="p-2 text-gray-600 hover:text-red-600 hover:bg-white rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Financial Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-600 font-medium">Acquis / Promis</p>
              <p className="text-2xl font-bold text-blue-900 mt-1">{formatAmount(parseFloat(commitment.commitment_amount.toString()))} €</p>
            </div>
            <div className="bg-emerald-50 p-4 rounded-lg">
              <p className="text-sm text-emerald-600 font-medium">Verse</p>
              <p className="text-2xl font-bold text-emerald-900 mt-1">{formatAmount(commitment.paid_total)} €</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <p className="text-sm text-orange-600 font-medium">Reste a verser</p>
              <p className="text-2xl font-bold text-orange-900 mt-1">{formatAmount(commitment.remaining)} €</p>
            </div>
          </div>

          {/* Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Categorie</p>
              <p className="font-medium text-gray-900">
                {commitment.category === 'operating' && 'Fonctionnement'}
                {commitment.category === 'investment' && 'Investissement'}
                {commitment.category === 'equity' && 'Fonds propres'}
                {commitment.category === 'loan' && 'Pret'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Periode conventionnee</p>
              <p className="font-medium text-gray-900">
                {commitment.convention_start_date && commitment.convention_end_date
                  ? `${formatDate(commitment.convention_start_date)} - ${formatDate(commitment.convention_end_date)}`
                  : 'Non definie'}
              </p>
            </div>
            <div className="col-span-2">
              <p className="text-sm text-gray-600">Affectation</p>
              <p className="font-medium text-gray-900">{commitment.allocation || 'Non specifiee'}</p>
            </div>
            {commitment.notes && (
              <div className="col-span-2">
                <p className="text-sm text-gray-600">Notes</p>
                <p className="text-gray-900">{commitment.notes}</p>
              </div>
            )}
          </div>

          {/* Disbursements History */}
          <div>
            <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Historique des versements ({commitment.disbursements.length})
            </h4>
            {commitment.disbursements.length > 0 ? (
              <div className="space-y-2">
                {commitment.disbursements.map(disbursement => (
                  <div key={disbursement.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-emerald-600">{formatAmount(parseFloat(disbursement.amount.toString()))} €</span>
                        <span className="text-sm text-gray-600">{formatDate(disbursement.paid_date)}</span>
                        {disbursement.reference && (
                          <span className="text-xs text-gray-500 px-2 py-1 bg-white rounded">Ref: {disbursement.reference}</span>
                        )}
                      </div>
                      {disbursement.notes && (
                        <p className="text-sm text-gray-600 mt-1">{disbursement.notes}</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteDisbursement(disbursement.id)}
                      className="p-2 text-gray-400 hover:text-red-600 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Aucun versement enregistre</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
